import { PAGE_PERMISSIONS } from "@consts";
import { replaceSpecialChars } from "@utils";
import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useMatch, useBlocker, useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import useAppStore from "src/stores/appStore";
import {
  hasPageErrorTypeSelector,
  hasPermissionSelector,
  systemModeSelector,
  userDataSelector,
  userPermissionsSelector
} from "src/stores/selectors/appSelectors";
import {
  speechPlayerSelector,
  playSpeechSelector,
  pauseSpeechSelector,
  resumeSpeechSelector,
  stopSpeechSelector,
  closeSpeechPlayerSelector,
  downloadCurrentAudioSelector
} from "src/stores/selectors/speechPlayerSelectors";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import { getErrorPageFromStatus } from "src/layout/ErrorPages/ErrorPages.helpers";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import { COLORS } from "src/components/admin/Settings/consts";
import { useDraft } from "src/queries/hooks/assistants";
import { adminService } from "src/services";

const useEffectAsync = (f, deps) => {
  useEffect(() => {
    f();

  }, deps);
};

const useIsAdminPanel = () => {
  const { pathname } = useLocation();
  const [isAdminPanel, setIsAdminPanel] = useState(pathname.includes("/admin"));

  useEffect(() => {
    setIsAdminPanel(pathname.includes("/admin"));
  }, [pathname]);

  return isAdminPanel;
};

const useIsCurrentPageReadonly = () => {
  const hasPermission = useAppStore(hasPermissionSelector);
  const userPermissions = useAppStore(userPermissionsSelector);

  const [isPageReadonly, setIsPageReadonly] = useState(false);
  const { pathname } = useLocation();
  const page = useCurrentAssistantPage();

  useEffect(() => {
    PAGE_PERMISSIONS[page]?.WRITE &&
      !hasPermission(PAGE_PERMISSIONS[page]?.WRITE) ?
      setIsPageReadonly(true) :
      setIsPageReadonly(false);
  }, [hasPermission, userPermissions, page, pathname]);
  return isPageReadonly;
};

export const useDisplayDashboardSettingsBanner = () => {
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const systemMode = useAppStore(systemModeSelector);
  const isBannerVisible = dashboardSettings?.banner?.show;
  const isMaintenanceBanner = dashboardSettings?.banner?.color === COLORS.MAINTENANCE && systemMode === SYSTEM_MODES.READ_ONLY;
  const isIncidentBanner = dashboardSettings?.banner?.color === COLORS.INCIDENT;
  return (isBannerVisible && (isMaintenanceBanner || isIncidentBanner));
};

export const useStatusBarDisplayed = () => {
  const isPageReadonly = useIsCurrentPageReadonly();
  const { data: draft } = useDraft();
  const systemMode = useAppStore(systemModeSelector);
  const displaySettingsBanner = useDisplayDashboardSettingsBanner();

  return displaySettingsBanner || (systemMode === SYSTEM_MODES.EDIT && (draft || isPageReadonly));
};

const useCurrentAssistantPage = () => {
  const matchPage = useMatch("/:accountId/:assistantId/:page/*");
  return matchPage?.params?.page ?? "";
};

const useCurrentAdminPage = () => {
  const matchPage = useMatch("/admin/*");
  return matchPage ? matchPage.params["*"] : null;
};

const useCurrentFullAssistantPage = () => {
  const matchPage = useMatch("/:accountId/:assistantId/*");
  return matchPage ? matchPage.params["*"] : null;
};

const useCurrentFullAccountPage = () => {
  const match = useMatch({ path: "/:accountId", end: true });
  const accountMatch = useMatch("/:accountId/*");

  if (match) {
    return "all-agents";
  }

  if (accountMatch) {
    return accountMatch.params["*"];
  }

  return [];
};

const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
};

const useAsyncError = () => {

  const [_, setError] = useState();
  return useCallback(
    (e) => {
      setError(() => {
        throw e;
      });
    },
    [setError]
  );
};

const useErrorPage = () => {
  const pageErrorType = useAppStore(hasPageErrorTypeSelector);

  if (pageErrorType === null) {
    return null;
  }
  return getErrorPageFromStatus(pageErrorType);
};

const useHasSystemModeChanged = ({ from = [], to = [] }) => {
  const isFirstRender = useRef(true);
  const systemMode = useAppStore(systemModeSelector);
  const previousSystemMode = useRef(null);
  const [hasChanged, setHasChanged] = useState({ value: false });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const isValidFrom = from.length === 0 || from.includes(previousSystemMode.current);
    const isValidTo = to.length === 0 || to.includes(systemMode);

    if (isValidFrom && isValidTo && previousSystemMode.current !== systemMode) {
      setHasChanged({ value: true });
    } else {
      setHasChanged({ value: false });
    }

    previousSystemMode.current = systemMode;
  }, [systemMode]);

  return hasChanged;
};

const getConfigurationId = replaceSpecialChars("_");

const useIsValidAssistantName = ({ allAssistantsData, assistantName }) => {
  const userData = useAppStore(userDataSelector);
  const [configurationIds, setConfigurationIds] = useState([]);
  const assistantNames = allAssistantsData.map(assistant => assistant.assistantName.toLowerCase());

  useEffect(() => {
    (async () => {
      setConfigurationIds(await adminService.configurationIds());
    })();
  }, [setConfigurationIds, userData]);

  const checkLettersValidation = (name) => /^[a-zA-Z].*$/.test(name) && name.trim().length > 0;
  const validateAssistantName = (name) => {
    if (!name || name.trim().length === 0 || name.length > 34) return false;
    return /^[a-zA-Z]([a-zA-Z0-9\- _()]*[a-zA-Z0-9()])?$/.test(name);
  };

  const trimmedName = assistantName?.trim() || "";

  const nameExists = assistantNames.includes(trimmedName.toLowerCase()) ||
    configurationIds.includes(getConfigurationId(trimmedName));
  const startsWithLetter = checkLettersValidation(trimmedName);
  const validName = validateAssistantName(trimmedName);
  const isValidAssistantName = !nameExists && startsWithLetter && validName;

  let assistantNameErrorMessage = "";
  if (!trimmedName) {
    assistantNameErrorMessage = "";
  } else if (!startsWithLetter) {
    assistantNameErrorMessage = "Agent name must start with a letter";
  } else if (!validName) {
    assistantNameErrorMessage = "Agent name may only include letters, numbers, or hyphens.";
  } else if (nameExists) {
    assistantNameErrorMessage = "Conflicts with an existing agent name";
  }

  return {
    isValidAssistantName,
    startsWithLetter,
    nameExists,
    assistantNameErrorMessage
  };
};

const useReactBlocker = (isDirty, setIsConfirmationDialogOpen, onNoChanges, allowedPathPatterns = []) => {
  const location = useLocation();
  const isNavigatingToAllowedPath = useRef(false);

  const shouldBlock = useCallback(
    ({ nextLocation }) => {
      // Ignore if only query params changed
      const samePath = nextLocation.pathname === location.pathname;

      if (!isDirty || samePath) {
        isNavigatingToAllowedPath.current = false;
        return false;
      }

      // Check if the destination path matches any allowed patterns
      const isAllowedPath = allowedPathPatterns.some(pattern => {
        if (typeof pattern === "string") {
          return nextLocation.pathname.includes(pattern);
        }
        if (pattern instanceof RegExp) {
          return pattern.test(nextLocation.pathname);
        }
        if (typeof pattern === "object" && pattern.include && pattern.exclude) {
          const matchesInclude = nextLocation.pathname.includes(pattern.include);
          const matchesExclude = pattern.exclude.some(excludePath =>
            nextLocation.pathname.includes(excludePath)
          );
          return matchesInclude && !matchesExclude;
        }
        return false;
      });

      isNavigatingToAllowedPath.current = isAllowedPath;

      return !isAllowedPath;
    },
    [isDirty, location.pathname, allowedPathPatterns]
  );

  const blocker = useBlocker(shouldBlock);

  const handleUnsavedChanges = useCallback(() => {
    if (isDirty) {
      setIsConfirmationDialogOpen(true);
    } else {
      onNoChanges?.();
      blocker.reset();
    }
  }, [isDirty, setIsConfirmationDialogOpen, blocker, onNoChanges]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      handleUnsavedChanges();
    }
  }, [blocker, isDirty, handleUnsavedChanges]);

  return [blocker, isNavigatingToAllowedPath];
};

const useSpeechPlayer = ({ instanceId }) => {
  const speechPlayer = useAppStore(speechPlayerSelector);
  const {
    isPlaying,
    isLoading,
    isPaused,
    isOpen,
    currentInstanceId,
    error,
    progress = 0,
    voiceName,
    text
  } = speechPlayer;
  const playSpeech = useAppStore(playSpeechSelector);
  const pauseSpeech = useAppStore(pauseSpeechSelector);
  const closeSpeechPlayer = useAppStore(closeSpeechPlayerSelector);
  const downloadCurrentAudio = useAppStore(downloadCurrentAudioSelector);
  const isCurrentInstance = currentInstanceId === instanceId;
  const isCurrentPlayerPlaying = isPlaying && isCurrentInstance;
  const isCurrentPlayerLoading = isLoading && isCurrentInstance;
  const isCurrentPlayerPaused = isPaused && isCurrentInstance;
  const isCurrentPlayerOpen = isOpen && isCurrentInstance;

  const handlePlayPause = ({ text }) => {
    if (isCurrentPlayerLoading) return;
    if (isCurrentPlayerPlaying) {
      pauseSpeech();
    } else {
      playSpeech({ text, instanceId });
    }
  };

  return {
    handlePlayPause,
    closeSpeechPlayer,
    downloadCurrentAudio,
    isOpen: isCurrentPlayerOpen,
    isPlaying: isCurrentPlayerPlaying,
    isPaused: isCurrentPlayerPaused,
    isLoading: isCurrentPlayerLoading,
    error,
    progress,
    voiceName,
    text
  };
};

export {
  useCurrentAdminPage,
  useCurrentAssistantPage,
  useCurrentFullAssistantPage,
  useCurrentFullAccountPage,
  useEffectAsync,
  useIsAdminPanel,
  useIsCurrentPageReadonly,
  useIsMobile,
  useAsyncError,
  useErrorPage,
  useHasSystemModeChanged,
  useIsValidAssistantName,
  useReactBlocker,
  useSpeechPlayer
};
